import { useEffect } from 'react';
import { Alert, Box, Layout } from '@zillow/constellation-10';
import PaymentsUpsell from '../Rent/PaymentsUpsell';
import type {
  YourRentalAlertDto,
  YourRentalCreditReportingSectionDto,
  YourRentalPaymentSectionDto,
  YourRentalRewardsSectionDto,
} from '@zg-rentals/ts-your-rental-api-client';
import { AlertTone } from '@zg-rentals/ts-your-rental-api-client';
import { emitRentReportingUpsellView } from '../../analytics/events';
import PaymentCard from '../Rent/PaymentCard';
import PaymentCardError from '../Rent/PaymentCardError';
import CreditClimbCard from '../CreditClimb/CreditClimbCard';
import TransactionsTableError from '../Rent/TransactionsTableError';
import TransactionsTable from '../Rent/TransactionsTable';
import { RewardsCard } from '../Rewards';
import { useYourRentalPageContext } from '../../contexts/YourRentalPageContext';

const TONE_MAP: Record<AlertTone, 'critical' | 'info' | 'success' | 'warning'> = {
  [AlertTone.CRITICAL]: 'critical',
  [AlertTone.INFO]: 'info',
  [AlertTone.SUCCESS]: 'success',
  [AlertTone.WARNING]: 'warning',
};

type RentProps = {
  paymentsSection?: YourRentalPaymentSectionDto;
  creditReportingSection?: YourRentalCreditReportingSectionDto;
  rewardsSection?: YourRentalRewardsSectionDto;
  listingAlias?: string;
  autoOpenRewardsEnroll?: boolean;
};

const Rent = ({
  paymentsSection,
  creditReportingSection,
  rewardsSection,
  listingAlias,
  autoOpenRewardsEnroll,
}: RentProps) => {
  const { renterProfile } = useYourRentalPageContext();
  const showUpsell = !creditReportingSection && (!paymentsSection || !!paymentsSection.upsellCardType);

  useEffect(() => {
    if (showUpsell) {
      emitRentReportingUpsellView(renterProfile);
    }
  }, [showUpsell, renterProfile]);

  if (showUpsell) {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          border: '{borderWidths.default} solid {colors.gray.300}',
          // oxlint-disable-next-line constellation/prefer-semantic-tokens -- pre-existing base-token usage; was warn-only under ESLint's pandaLint
          borderRadius: '{radii.300}',
        }}
        data-testid="RentReporting"
      >
        <PaymentsUpsell listingAlias={listingAlias} />
      </Box>
    );
  }

  const { paymentCard, transactionSection, error, alerts } = paymentsSection ?? {};

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'loose',
      }}
      data-testid="RentReporting"
    >
      {alerts && alerts.length > 0 && (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }} data-testid="RentReporting-Alerts">
          {alerts.map((alert: YourRentalAlertDto, index: number) => (
            <Alert key={index} tone={TONE_MAP[alert.tone]}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}
      <Layout.Root template="fluid12">
        {paymentCard && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6', xxl: 'span 4' }, padding: '0' }}>
            <PaymentCard paymentCard={paymentCard} />
          </Layout.Content>
        )}
        {error && !paymentCard && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6', xxl: 'span 4' }, padding: '0' }}>
            <PaymentCardError error={error} />
          </Layout.Content>
        )}
        {creditReportingSection?.creditClimbCard && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6', xxl: 'span 4' }, padding: '0' }}>
            <CreditClimbCard creditClimbCard={creditReportingSection.creditClimbCard} />
          </Layout.Content>
        )}
        {rewardsSection && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6' }, padding: '0' }}>
            <RewardsCard rewardsSection={rewardsSection} autoOpenEnroll={autoOpenRewardsEnroll} />
          </Layout.Content>
        )}
      </Layout.Root>
      {transactionSection && (
        <>
          <Box css={{ display: { base: 'block', lg: 'none' } }}>
            <TransactionsTable transactionSection={transactionSection} listingAlias={listingAlias} isDesktop={false} />
          </Box>
          <Box css={{ display: { base: 'none', lg: 'block' } }}>
            <TransactionsTable transactionSection={transactionSection} listingAlias={listingAlias} isDesktop={true} />
          </Box>
        </>
      )}
      {error && !transactionSection && <TransactionsTableError error={error} />}
    </Box>
  );
};

export default Rent;
